import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import Navbar from '../../Navbar';
import Footer from '../../Footer';
import SimuladorCNH from '../SimuladorCNH';
import ToolCta from '../ToolCta';
import { fadeInUp } from '../../../lib/animations';

const TOOL_SLUG = 'simulador-pontos-cnh';

/** Página /ferramentas/simulador-pontos-cnh */
const CnhPage: React.FC = () => (
  <>
    <Helmet>
      <title>Simulador de Pontos da CNH 2026 – Limite e Suspensão | Gas2</title>
      <meta
        name="description"
        content="Simule sua pontuação na CNH na janela de 12 meses. Descubra o risco de suspensão: limite de 40 pontos para EAR (motoristas profissionais) e 20 pontos para condutores comuns. Gratuito."
      />
      <link rel="canonical" href={`https://gas2.app/ferramentas/${TOOL_SLUG}`} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Gas2" />
      <meta property="og:url" content={`https://gas2.app/ferramentas/${TOOL_SLUG}`} />
      <meta property="og:title" content="Simulador de Pontos da CNH 2026 – Gas2" />
      <meta
        property="og:description"
        content="Simule sua pontuação na CNH: limite de 40 pts para EAR e 20 pts para condutores comuns, janela de 12 meses e risco de suspensão. Atualizado com a nova lei de trânsito."
      />
      <meta property="og:image" content="https://gas2.app/logo512.png" />
      <meta property="og:locale" content="pt_BR" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Simulador de Pontos da CNH 2026 – Gas2" />
      <meta
        name="twitter:description"
        content="Calcule seus pontos na CNH na janela de 12 meses e saiba o risco de suspensão."
      />
      <meta name="twitter:image" content="https://gas2.app/logo512.png" />
    </Helmet>

    <Navbar solid />

    <main className="min-h-screen bg-slate-950 pt-24 pb-20">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 space-y-10">

        {/* ── H1 + intro ── */}
        <motion.header
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          className="space-y-4"
        >
          <span className="inline-block rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-600">
            Legislação de Trânsito
          </span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white leading-tight">
            Simulador de Pontos da CNH — Limite e Risco de Suspensão
          </h1>
          <p className="text-slate-400 text-base sm:text-lg leading-relaxed">
            Adicione suas infrações e veja em tempo real quantos pontos você acumulou na{' '}
            <strong className="text-slate-200">janela deslizante de 12 meses</strong>, o
            limite aplicável e o risco de suspensão da carteira.
          </p>
        </motion.header>

        {/* ── Simulador (estado persistido no localStorage) ── */}
        <SimuladorCNH />

        {/* ── Conteúdo SEO ── */}
        <motion.article
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="space-y-8"
        >
          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              Como Funciona a Contagem de Pontos na CNH?
            </h2>
            <p className="text-slate-400 leading-relaxed mt-3">
              Desde a reforma do Código de Trânsito Brasileiro (Lei 14.071/2020, em vigor
              desde 2021), os pontos são contados em uma{' '}
              <strong className="text-slate-200">janela deslizante de 12 meses</strong>,
              não mais em períodos fixos do ano. Cada infração possui uma pontuação:
            </p>
            <ul className="mt-3 space-y-1.5 text-slate-400 text-sm">
              {[
                { g: 'Leve', p: 3, ex: 'Avançar sinal amarelo, usar buzina indevidamente' },
                { g: 'Média', p: 4, ex: 'Estacionar em local proibido, não usar cinto de segurança' },
                { g: 'Grave', p: 5, ex: 'Usar celular ao volante, não respeitar faixa de pedestres' },
                { g: 'Gravíssima', p: 7, ex: 'Dirigir embriagado, racha, excesso de velocidade acima de 50%' },
              ].map(row => (
                <li key={row.g} className="flex gap-2">
                  <span className="shrink-0 font-semibold text-white w-24">{row.g} ({row.p} pts)</span>
                  <span>{row.ex}</span>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              Qual É o Limite de Pontos da CNH?
            </h2>
            <p className="text-slate-400 leading-relaxed mt-3">
              O limite depende do perfil do condutor:
            </p>
            <div className="mt-4 space-y-3">
              {[
                { limit: '40 pontos', cond: 'Condutor com EAR — usa a CNH para atividade profissional remunerada', color: 'border-emerald-400/30 text-emerald-300' },
                { limit: '20 pontos', cond: 'Condutor comum — uso particular da habilitação', color: 'border-red-400/30 text-red-300' },
              ].map(row => (
                <div key={row.limit} className={`rounded-xl border ${row.color} bg-slate-900 p-4 flex gap-3`}>
                  <span className="text-lg font-bold shrink-0">{row.limit}</span>
                  <span className="text-slate-400 text-sm">{row.cond}</span>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              O que é EAR — Atividade Remunerada com a CNH?
            </h2>
            <p className="text-slate-400 leading-relaxed mt-3">
              Condutores que usam a CNH para fins profissionais remunerados são classificados
              como <strong className="text-slate-200">EAR (Exerce Atividade Remunerada)</strong>.
              Isso inclui motoristas de aplicativo (Uber, 99, inDriver), taxistas, caminhoneiros,
              motoristas escolares e de vans. Para esses condutores, o limite é de{' '}
              <strong className="text-slate-200">40 pontos</strong>, enquanto condutores comuns
              (uso particular da CNH) têm limite de{' '}
              <strong className="text-slate-200">20 pontos</strong>.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              Quando a CNH é Suspensa?
            </h2>
            <p className="text-slate-400 leading-relaxed mt-3">
              Ao atingir ou ultrapassar o limite de pontos, o DETRAN notifica o condutor. A
              suspensão pode variar de{' '}
              <strong className="text-slate-200">6 meses a 2 anos</strong>, dependendo dos
              Antecedentes. Em caso de reincidência, pode haver cassação definitiva. O condutor
              tem direito a defesa prévia e recurso em segunda instância. Utilize este
              simulador para monitorar sua pontuação antes de atingir o limite.
            </p>
          </section>
        </motion.article>

        {/* ── CTA download ── */}
        <ToolCta toolSlug={TOOL_SLUG} />
      </div>
    </main>

    <Footer />
  </>
);

export default CnhPage;





